import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import en from '../locales/en.json';
import nl from '../locales/nl.json';
import enAssistant from '../locales/assistant.en.json';
import nlAssistant from '../locales/assistant.nl.json';
import enClosedBeta from '../locales/closedBeta.en.json';
import nlClosedBeta from '../locales/closedBeta.nl.json';
import enConsumerCopy from '../locales/consumerCopy.en.json';
import nlConsumerCopy from '../locales/consumerCopy.nl.json';
import enDayDate from '../locales/dayDate.en.json';
import nlDayDate from '../locales/dayDate.nl.json';

const enTranslations = { ...en, ...enDayDate, ...enConsumerCopy, ...enClosedBeta, ...enAssistant };
const nlTranslations = { ...nl, ...nlDayDate, ...nlConsumerCopy, ...nlClosedBeta, ...nlAssistant };

export type Locale = 'en' | 'nl';
export type TranslationKey = keyof typeof enTranslations;

const translations: Record<Locale, Record<TranslationKey, string>> = { en: enTranslations, nl: nlTranslations };
const LOCALE_STORAGE_KEY = 'doddledays.locale';

export function resolveLocale(value: string | null | undefined): Locale {
  return value?.toLowerCase().startsWith('nl') ? 'nl' : 'en';
}

export type TranslationValues = Record<string, string | number>;

export function translate(locale: Locale, key: TranslationKey, values: TranslationValues = {}): string {
  const template = translations[locale][key] ?? translations.en[key];
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (
    Object.hasOwn(values, name) ? String(values[name]) : match
  ));
}

export function translateCurrent(key: TranslationKey, values: TranslationValues = {}): string {
  if (typeof window === 'undefined') return translate('en', key, values);
  const locale = resolveLocale(window.localStorage?.getItem(LOCALE_STORAGE_KEY) ?? window.navigator?.language);
  return translate(locale, key, values);
}

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, values?: TranslationValues) => string;
};

const defaultI18n: I18nContextValue = {
  locale: 'en',
  setLocale: () => undefined,
  t: (key, values) => translate('en', key, values),
};

const I18nContext = createContext<I18nContextValue>(defaultI18n);

function initialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  return resolveLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY) ?? window.navigator.language);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    setLocale: setLocaleState,
    t: (key, values) => translate(locale, key, values),
  }), [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}
